<script lang="ts">
/**
 * Key-value metadata row for item detail views.
 *
 * Displays a caption label on the left and a value on the right,
 * with fallback text when the value is empty. Composes Saltbox list
 * primitives internally.
 *
 * @example
 * ```vue
 * <SbList bordered separator>
 *   <DetailRow label="Brand" value="DeWalt" />
 *   <DetailRow label="Serial" value="DCF887B" mono />
 *   <DetailRow label="Notes" />
 * </SbList>
 * ```
 */
export interface DetailRowProps {
  /** The field label. */
  label: string
  /** The field value. When absent, `fallback` is shown. */
  value?: string | null
  /** Text shown when `value` is empty. @defaultValue `"Not set"` */
  fallback?: string
  /** Render the value in monospace font. @defaultValue `false` */
  mono?: boolean
  /** Optional secondary label beneath the primary label. */
  subLabel?: string
}
</script>

<script setup lang="ts">
import { SbListItem, SbListSection, SbListLabel } from "@unio/saltbox-ui"
import { useSlots } from "vue"

withDefaults(defineProps<DetailRowProps>(), {
  fallback: "Not set",
})

const slots = useSlots()
</script>

<template>
  <SbListItem>
    <SbListSection>
      <SbListLabel caption>{{ label }}</SbListLabel>
      <SbListLabel v-if="subLabel" caption class="text-xs">{{ subLabel }}</SbListLabel>
    </SbListSection>
    <SbListSection side>
      <slot v-if="slots.default" />
      <span
        v-else
        :class="[value ? 'text-highlighted' : 'text-muted', mono ? 'font-mono text-xs' : 'text-sm']"
      >
        {{ value ?? fallback }}
      </span>
    </SbListSection>
  </SbListItem>
</template>
